import React, { useCallback, useEffect, useState } from 'react';
import { Layers, CheckCircle2, AlertCircle, Save } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { apiFetch } from '../../services/apiClient';
import PositionsCard from './PositionsCard';
import OnCallRolesCard from './OnCallRolesCard';
import PageHeader from '../ui/PageHeader';
import { ControlBar, ToggleGroup } from '../ui/ControlBar';

// =============================================================================
// Ranks & Positions -- what each rank and on-call grade NUMBER is called, the
// on-call roles each grade fills, and the department's list of positions.
// =============================================================================
// A person's clinical rank (0..4) and on-call grade (0..2) are numbers set on
// their own record, in the staff drawer (Oct 2026), and are what rostering
// reads. A position is display only; its rank and grade are suggestions that
// fill in when it is picked (PositionsCard). The title -> rank maps this page
// used to edit were retired: each person's rank and grade were written onto
// them once from those maps.
// =============================================================================

const SCOPES = {
  clinical: { label: 'Clinical Ranks', ceiling: 4,
    blurb: 'Seniority for clinical scheduling, 0 the most senior. Slots say which ranks may fill them.' },
  oncall: { label: 'On-Call Grades', ceiling: 2,
    blurb: 'Which on-call pool somebody is in. Somebody with no grade takes no on-call.' },
};

const RankNamesEditor = ({ scope, labels, setLabel, onSave, saving, dirty }) => {
  const cfg = SCOPES[scope];
  const ranks = Array.from({ length: cfg.ceiling + 1 }, (_, r) => r);
  return (
    <Card padding="p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 tracking-tight">{cfg.label} — Names</h3>
          <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed max-w-xl">
            {cfg.blurb} Name each number however your department prefers; the number is what counts.
          </p>
        </div>
        <Button onClick={() => onSave(scope)} icon={Save} disabled={saving || !dirty} isLoading={saving}>Save Names</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {ranks.map((r) => (
          <div key={r} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5">
            <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-slate-100 text-[11px] font-black text-slate-600 tabular-nums">{r}</span>
            <input type="text" aria-label={`${cfg.label} ${r} name`}
              value={labels?.[String(r)] ?? ''}
              onChange={(e) => setLabel(scope, r, e.target.value)}
              placeholder={`Rank ${r} name`}
              className="w-36 px-2 py-1 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-md outline-none focus:border-[var(--primary)]" />
          </div>
        ))}
      </div>
    </Card>
  );
};

const RankMapPage = ({ onRolesSaved }) => {
  const [labelsByScope, setLabelsByScope] = useState({ clinical: {}, oncall: {} });
  const [labelSnapshot, setLabelSnapshot] = useState({ clinical: '', oncall: '' });
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScope, setActiveScope] = useState('clinical');
  const [banner, setBanner] = useState({ type: null, message: '' });

  const apply = (data) => {
    const clinical = data.clinicalRankLabels || data.clinical_rank_labels || {};
    const oncall = data.oncallRankLabels || data.oncall_rank_labels || {};
    setLabelsByScope({ clinical, oncall });
    setLabelSnapshot({ clinical: JSON.stringify(clinical), oncall: JSON.stringify(oncall) });
  };

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/rank-maps');
      apply(await res.json());
    } catch {
      setBanner({ type: 'error', message: 'Could not load the rank names.' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const setLabel = (scope, rank, value) =>
    setLabelsByScope(prev => ({ ...prev, [scope]: { ...(prev[scope] || {}), [String(rank)]: value } }));
  const dirty = (scope) => JSON.stringify(labelsByScope[scope] || {}) !== labelSnapshot[scope];

  const save = async (scope) => {
    setSaving(scope);
    setBanner({ type: null, message: '' });
    try {
      const res = await apiFetch(`/api/rank-maps/labels/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labels: labelsByScope[scope] || {} }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setBanner({ type: 'error', message: data.message || 'Save failed.' });
        return;
      }
      // Keep the other scope's unsaved edits: only this scope came back saved.
      const saved = scope === 'clinical'
        ? (data.clinicalRankLabels || data.clinical_rank_labels)
        : (data.oncallRankLabels || data.oncall_rank_labels);
      setLabelsByScope(prev => ({ ...prev, [scope]: saved || prev[scope] }));
      setLabelSnapshot(prev => ({ ...prev, [scope]: JSON.stringify(saved || labelsByScope[scope]) }));
      setBanner({ type: 'success', message: `${SCOPES[scope].label} names saved.` });
    } catch {
      setBanner({ type: 'error', message: 'Save failed — network error.' });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="animate-in fade-in space-y-6">
      <PageHeader icon={Layers} title="Ranks & Positions" />

      <ControlBar>
        <ToggleGroup label="Mode" value={activeScope} onChange={setActiveScope}
          options={[{ id: 'clinical', label: 'Clinical' }, { id: 'oncall', label: 'On-Call' }]} />
      </ControlBar>

      {banner.type && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold ${banner.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
          {banner.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{banner.message}</span>
        </div>
      )}

      {loading
        ? <div className="py-16 text-center text-xs font-bold text-slate-300 uppercase tracking-widest">Loading…</div>
        : <RankNamesEditor scope={activeScope} labels={labelsByScope[activeScope]} setLabel={setLabel}
            onSave={save} saving={saving === activeScope} dirty={dirty(activeScope)} />}

      {/* The duties each on-call grade covers, next to the grades' names
          (Oct 2026; moved from System Settings). */}
      {!loading && activeScope === 'oncall' && (
        <OnCallRolesCard onSaved={onRolesSaved} gradeLabels={labelsByScope.oncall || {}} />
      )}

      {!loading && (
        <PositionsCard rankLabels={labelsByScope.clinical} gradeLabels={labelsByScope.oncall} onBanner={setBanner} />
      )}
    </div>
  );
};

export default RankMapPage;
